import {RampIdType} from '../../../generated/graphql';
import {type RampKey} from '../../useRampFlag';
import {
  type EntityFlagsState,
  useEntityFlagsStore,
} from './EntityFeatureFlagStore';
import {applyFeatureFlagOverride} from './featureFlagOverrides';
import {type FlagMap, type OrgFlagMaps} from './flagMap';
import {isIntegrationBypassActive} from './sharedEvaluators';
import {
  useViewerFlagsStore,
  type ViewerFlagsState,
} from './ViewerFeatureFlagStore';
import {
  useViewerOrgFlagsStore,
  type ViewerOrgFlagsState,
} from './ViewerOrgFeatureFlagStore';

/**
 * Store-backed reader hooks that mirror the Apollo primitives in
 * `useRampFlag.ts`. They subscribe to the route-hydrated Zustand stores in
 * `./stores/*FeatureFlagStore.ts` and preserve each primitive's tri-state
 * shape (`false` unhydrated / `undefined` hydrated-but-missing / explicit
 * `true`|`false`) so callers relying on `?? true` / `?? false` defaults
 * behave identically to the Apollo path.
 */

// --- Viewer (user) flags ---------------------------------------------------

/**
 * Store-backed `useUserRampFlag`. Returns `undefined` when the server did not
 * return an entry for `rampKey.
 */
export function useUserFlagFromStore(
  rampKey: RampKey,
  rampIDType: RampIdType
): boolean | undefined {
  // Narrow the selector to the specific key's value so Zustand's Object.is
  // equality only wakes subscribers whose flag actually changed - not every
  // subscriber when the underlying map ref is replaced by the hydrate.
  return useViewerFlagsStore(state => {
    const map = selectViewerFlags(state, rampIDType);
    if (map == null) {
      // Unhydrated: match Apollo primitive's "no data yet" shape (false, not undefined).
      return false;
    }
    const isEnabled = map[rampKey];
    return applyFeatureFlagOverride(rampIDType, rampKey, isEnabled);
  });
}

function selectViewerFlags(
  state: ViewerFlagsState,
  rampIDType: RampIdType
): FlagMap | null {
  switch (rampIDType) {
    case RampIdType.UserId:
      return state.flagsByUserId;
    case RampIdType.UserName:
      return state.flagsByUserName;
    default:
      return null;
  }
}

// --- Viewer org flags ------------------------------------------------------

/**
 * Store-backed equivalent of `useOrgRampFlag`. Mirrors the Apollo primitive's
 * `!loading && data` collapse so unhydrated reads as `false`, a hydrated
 * "no entry for this key" reads as `undefined`, and `true`/`false` pass
 * through. Preserves the tri-state so callers relying on `?? true` /
 * `?? false` defaults behave identically.
 */
export function useOrgFlagFromStore(
  rampKey: RampKey,
  rampIDType: RampIdType
): boolean | undefined {
  // Narrow the selector to the specific key so Zustand's Object.is equality
  // only wakes subscribers whose flag actually changed. The tri-state
  // (`false` unhydrated / `undefined` hydrated-but-missing / explicit
  // `true`|`false`) is load-bearing for `?? true`/`?? false` call sites, so
  // it's preserved verbatim inside the selector.
  return useViewerOrgFlagsStore(state => {
    const maps = selectOrgMaps(state, rampIDType);
    if (maps == null) {
      // Unhydrated: match Apollo primitive's "no data yet" shape (false, not undefined).
      return false;
    }
    const isEnabled = maps.anyOrgEnabledMap[rampKey];
    return applyFeatureFlagOverride(rampIDType, rampKey, isEnabled);
  });
}

/**
 * Store-backed equivalent of `useOrgIdRampFlag`: scopes to a specific org.
 * When `orgId` is falsy, falls back to the unscoped "any org" behavior.
 * Returns `false` when the org isn't in the cache (matches the legacy
 * `specificOrg == null` branch) and `undefined` when the org is there but
 * the key isn't set on it.
 */
export function useOrgIdFlagFromStore(
  rampKey: RampKey,
  rampIDType: RampIdType,
  orgId: string | null = null
): boolean | undefined {
  // Narrow the selector to the specific key (scoped to orgId when provided)
  // so subscribers only wake when their specific value changes. Preserves
  // the tri-state the `?? true`/`?? false` call sites rely on.
  return useViewerOrgFlagsStore(state => {
    const maps = selectOrgMaps(state, rampIDType);
    if (maps == null) {
      return false;
    }
    let isEnabled: boolean | undefined;
    if (orgId != null && orgId.trim() !== '') {
      const orgMap = maps.perOrgId[orgId];
      if (orgMap == null) {
        isEnabled = false;
      } else {
        isEnabled = orgMap[rampKey];
      }
    } else {
      isEnabled = maps.anyOrgEnabledMap[rampKey];
    }
    return applyFeatureFlagOverride(rampIDType, rampKey, isEnabled);
  });
}

function selectOrgMaps(
  state: ViewerOrgFlagsState,
  rampIDType: RampIdType
): OrgFlagMaps | null {
  switch (rampIDType) {
    case RampIdType.OrgId:
      return state.flagsByOrgId;
    case RampIdType.OrgName:
      return state.flagsByOrgName;
    default:
      return null;
  }
}

// --- Entity flags ----------------------------------------------------------

/** Store-backed equivalent of `useEntityRampFlag` for the active entity which comes from the URL. */
export function useEntityFlagFromStore(
  rampKey: RampKey,
  rampIDType: RampIdType
): boolean {
  // Narrow the selector to the specific key so Zustand's Object.is equality
  // only wakes subscribers whose flag actually changed - the map reference
  // is replaced on every hydrate but most keys are unchanged. The
  // `envIsIntegration` bypass stays on the reader path: it's environmental,
  // not state-derived, and shouldn't drive subscription equality.
  const hydrated = useEntityFlagsStore(
    state => selectEntityFlags(state, rampIDType) != null
  );
  // undefined when store is not hydrated
  const isEnabledFromStore = useEntityFlagsStore(state => {
    const map = selectEntityFlags(state, rampIDType);
    return map == null ? undefined : map[rampKey];
  });

  if (!hydrated) {
    if (isIntegrationBypassActive(rampKey)) {
      return true;
    }
    return false;
  }

  let isEnabled = isEnabledFromStore ?? false;
  if (isIntegrationBypassActive(rampKey)) {
    isEnabled = true;
  }
  return applyFeatureFlagOverride(rampIDType, rampKey, isEnabled);
}

function selectEntityFlags(
  state: EntityFlagsState,
  rampIDType: RampIdType
): FlagMap | null {
  switch (rampIDType) {
    case RampIdType.EntityId:
      return state.flagsByEntityId;
    case RampIdType.EntityName:
      return state.flagsByEntityName;
    default:
      return null;
  }
}

// --- Composite readers -----------------------------------------------------

export function useUserOrOrgFlagFromStore(
  userRampKey: RampKey,
  orgRampKey: RampKey
): boolean {
  const userFlag = useUserFlagFromStore(userRampKey, RampIdType.UserName);
  const orgFlag = useOrgFlagFromStore(orgRampKey, RampIdType.OrgName);
  return userFlag || orgFlag || false;
}
